import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AutocompleteRenderGroupParams, Box, Collapse } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Selectable as BaseSelectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';

import { Fonts } from '../../styles/Variables';
import { ColorOption, TypeOption } from '../../utils/colors';
import { CheckboxWithLabel } from '../CheckboxWithLabel';
import { SomeCheckedIcon } from '../CheckboxWithLabel/CustomCheckbox';
import { Chip } from '../Chip';
import { CollapseIcon } from '../CollapseIcon';
import { IconButton } from '../IconButton';
import { SelectComponent } from '../Select/Select';

const PREFERRED_GROUP = 'preferred';
const OTHER_GROUP = 'other';

interface Selectable extends BaseSelectable {
  preferred?: boolean;
  group?: string;
}

type Props = {
  'data-cy'?: string;
  className?: string;
  color?: ColorOption;
  disabled?: boolean;
  error?: string | boolean;
  items: Selectable[];
  itemsDisabled?: string[];
  itemsEnabled?: string[];
  label?: String;
  limitTags?: number;
  loading?: boolean;
  onChange: (items: Selectable[]) => void;
  placeholder?: string;
  placeholderV2?: boolean;
  required?: boolean;
  size?: 'xs' | 'small' | 'medium';
  type?: TypeOption;
  value?: Selectable[];
};

const CollapseGroup = ({
  disabled,
  onCollapse,
  onSelect,
  open,
  params,
  selected,
  someChecked,
}: {
  disabled?: boolean;
  onCollapse: () => void;
  onSelect: () => void;
  open: boolean;
  params: AutocompleteRenderGroupParams;
  selected: boolean;
  someChecked: boolean;
}) => (
  <Box key={`language-collapse-${params.key}`}>
    <div
      className={classNames('flex spaced', !disabled && 'clickable')}
      role="presentation"
      onClick={(evt) => {
        evt.preventDefault();
        if (!disabled) onCollapse();
      }}
    >
      <CheckboxWithLabel
        checked={selected}
        disabled={disabled}
        icon={someChecked ? <SomeCheckedIcon /> : undefined}
        label={
          params.group === PREFERRED_GROUP
            ? translate('global.common')
            : translate('global.other')
        }
        onChange={() => onSelect()}
      />
      <IconButton
        disabled={disabled}
        onClick={(evt) => {
          evt.preventDefault();
          onCollapse();
        }}
        size="small"
      >
        <CollapseIcon open={open} color={disabled ? 'disabled' : undefined} />
      </IconButton>
    </div>
    <Collapse in={open}>{params.children}</Collapse>
  </Box>
);

const useStyles = makeStyles({
  chipMargin: {
    margin: '0.25rem',
  },
  internalLabelCommon: {
    alignItems: 'center',
    display: 'flex',
    lineHeight: '20px',
    marginTop: 1,
    maxWidth: 'calc(100% - 30px)',

    '& .placeholder-chip-counter.disabled': {
      opacity: 0.6,
    },
  },
  internalSublabel: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
  },
  internalLabel: {
    fontSize: `calc(${Fonts.fontSize} * 1.125)`,
  },
  internalLabelSmall: {
    fontSize: Fonts.fontSize,
  },
  internalLabelExtraSmall: {
    fontSize: `calc(${Fonts.fontSize} * 0.875)`,
  },
});

export const SelectLanguage = ({
  disabled,
  items: itemsProp,
  itemsDisabled,
  itemsEnabled,
  onChange,
  placeholder,
  size = 'medium',
  value,
  ...rest
}: Props) => {
  const styles = useStyles();
  const items = useMemo(
    () => itemsProp.filter(({ disabled: isItemDisabled }) => !isItemDisabled),
    [itemsProp],
  );

  const [collapsed, setCollapsed] = useState<{ [x: string]: boolean }>({});
  const [search, setSearch] = useState<string>('');
  const [convertedValue, setConvertedValue] = useState<Selectable[]>([]);

  const groupedItems = useMemo(() => {
    const filtered = items.filter((item) => item.value !== '_SEPARATOR_');
    return filtered.map((item) => ({
      ...item,
      group: item.preferred ? PREFERRED_GROUP : OTHER_GROUP,
    }));
  }, [items]);

  const filterItemBySearch = useCallback(
    (query: string) => (item: Selectable) =>
      !query ||
      (typeof item.label === 'string' &&
        item.label.toLocaleLowerCase().includes(query?.toLocaleLowerCase())) ||
      (typeof item.value === 'string' &&
        item.value.toLocaleLowerCase().includes(query?.toLocaleLowerCase())),
    [],
  );

  const filterOptions = useCallback(
    (data: Selectable[], options: any) =>
      data.filter(filterItemBySearch(options.inputValue)),
    [filterItemBySearch],
  );

  const handleChange = useCallback(
    (data: Selectable[]) => {
      setConvertedValue(data);
      onChange(data);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (inputValue: string, reason: string | undefined) => {
      if (reason !== 'reset') {
        setSearch(inputValue);
      }
      if (inputValue?.length) {
        setCollapsed({
          [PREFERRED_GROUP]: true,
          [OTHER_GROUP]: true,
        });
      }
      if (reason === 'blur') {
        setCollapsed({});
      }
    },
    [],
  );

  const getItemGroup = useCallback(
    (item: Selectable) => item.group || OTHER_GROUP,
    [],
  );

  const isOptionEqual = useCallback(
    (a: Selectable, b: Selectable) => a.value === b.value,
    [],
  );

  const handleRenderGroup = useCallback(
    (paramsGroup: AutocompleteRenderGroupParams) => {
      const selectedItems = convertedValue?.filter(
        (item: Selectable) =>
          (item.group ||
            groupedItems.find((g) => g.value === item.value)?.group) ===
          paramsGroup.group,
      );

      const isSelected = !!selectedItems?.length;

      const groupItems = groupedItems.filter(
        (item) => item.group === paramsGroup.group,
      );

      const someChecked =
        selectedItems?.length > 0 && selectedItems.length < groupItems.length;

      const handleCollapseToggle = () => {
        setCollapsed({
          ...collapsed,
          [paramsGroup.group]: !collapsed[paramsGroup.group],
        });
      };

      const handleGroupSelect = () => {
        if (isSelected) {
          // Deselect all items in this group
          handleChange(
            convertedValue?.filter((item) => {
              const itemGroup = groupedItems.find((g) => g.value === item.value)
                ?.group;
              return (
                itemGroup !== paramsGroup.group ||
                !filterItemBySearch(search)(item)
              );
            }),
          );
        } else {
          // Select all items in this group
          const groupItemsToAdd = groupedItems.filter(
            (item) =>
              item.group === paramsGroup.group &&
              filterItemBySearch(search)(item) &&
              !convertedValue.some((v) => v.value === item.value),
          );
          handleChange([...convertedValue, ...groupItemsToAdd]);
        }
      };

      return (
        <CollapseGroup
          disabled={disabled}
          key={`language-collapse-group-${paramsGroup.group}-${search}`}
          params={paramsGroup}
          open={!!collapsed[paramsGroup.group]}
          onCollapse={handleCollapseToggle}
          onSelect={handleGroupSelect}
          selected={isSelected}
          someChecked={someChecked}
        />
      );
    },
    [
      collapsed,
      convertedValue,
      disabled,
      filterItemBySearch,
      groupedItems,
      handleChange,
      search,
    ],
  );

  const renderOption = useCallback(
    (option: Selectable) => {
      const isSelected =
        convertedValue?.findIndex((item) => item.value === option.value) !== -1;

      const handleOptionChange = (checked: boolean) => {
        if (checked) {
          handleChange([...convertedValue, option]);
        } else {
          handleChange(
            convertedValue?.filter((item) => item.value !== option.value),
          );
        }
      };

      return (
        <CheckboxWithLabel
          checked={isSelected}
          key={`language-item-${option.value}`}
          label={option.label}
          onChange={handleOptionChange}
        />
      );
    },
    [convertedValue, handleChange],
  );

  const renderTags = useCallback(
    (tagValue: any[]) => {
      if (!tagValue?.length) {
        return <></>;
      }

      if (placeholder) {
        return (
          <span
            className={classNames(
              styles.internalLabelCommon,
              size === 'medium' && styles.internalLabel,
              size === 'small' && styles.internalLabelSmall,
              size === 'xs' && styles.internalLabelExtraSmall,
              'semi-bold',
            )}
          >
            <span className={styles.internalSublabel}>{placeholder}</span>
            &nbsp;
            {!disabled && (
              <Chip
                className={classNames(
                  'semi-bold placeholder-chip-counter',
                  disabled && 'disabled',
                )}
                color="secondary"
                type="contained-2"
                size={size === 'xs' ? 'small' : 'medium'}
              >
                {tagValue.length}
              </Chip>
            )}
          </span>
        );
      }

      return tagValue.map((item: Selectable) => (
        <Chip
          className={classNames(
            styles.chipMargin,
            'semi-bold placeholder-chip-counter',
            disabled && 'disabled',
          )}
          color="secondary"
          key={`language-tag-${item.value}`}
          type="contained-2"
          size={size === 'xs' ? 'small' : 'medium'}
        >
          {item.label}
        </Chip>
      ));
    },
    [placeholder, disabled, styles, size],
  );

  useEffect(() => {
    setConvertedValue(value || []);
  }, [value]);

  return (
    <SelectComponent
      disabled={disabled}
      filterOptions={filterOptions}
      grouped={getItemGroup}
      isOptionEqualToValue={isOptionEqual}
      items={groupedItems}
      multiple
      onChange={handleChange}
      onInputChange={handleInputChange}
      placeholder={placeholder}
      renderGroup={handleRenderGroup}
      renderOption={renderOption}
      renderTags={renderTags}
      size={size}
      value={convertedValue}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
};

export default SelectLanguage;
